import { useCallback, useEffect, useMemo, useState } from 'react';
import { DOPPLER_CONFIG } from '../config/dopplerRadar.config.js';
import {
  generateFrameTimes,
  getIntervalMs,
  getPlaybackFrameIntervalMs,
  roundToNearestIntervalMinutes,
} from '../utils/dopplerFrame.utils.js';

const FRAME_INTERVAL_MINUTES = DOPPLER_CONFIG.frameIntervalMinutes;
const HISTORY_FRAME_COUNT = DOPPLER_CONFIG.historyFrameCount;
const FUTURE_FRAME_COUNT = DOPPLER_CONFIG.futureFrameCount;
const FRAME_COUNT = HISTORY_FRAME_COUNT + FUTURE_FRAME_COUNT + 1;
const CURRENT_FRAME_INDEX = HISTORY_FRAME_COUNT;
const FRAME_INTERVAL_MS = getIntervalMs(FRAME_INTERVAL_MINUTES);

export default function useDopplerPlayback({ isPlaybackReady = false, isActive = true } = {}) {
  const [timeAnchor, setTimeAnchor] = useState(() =>
    roundToNearestIntervalMinutes(new Date(), FRAME_INTERVAL_MINUTES),
  );
  const [displayedFrameIndex, setDisplayedFrameIndex] = useState(() => CURRENT_FRAME_INDEX);
  const [requestedFrameIndex, setRequestedFrameIndex] = useState(() => CURRENT_FRAME_INDEX);
  const [isPlaying, setIsPlaying] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Pause the animation loop when the browser tab is hidden. Combined with the
  // widget-viewport `isActive` flag, this stops the continuous frame-advance /
  // tile churn whenever the radar is not actually being looked at.
  const [isPageVisible, setIsPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState !== 'hidden');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const playbackActive = isActive && isPageVisible;

  const frames = useMemo(
    () => generateFrameTimes(timeAnchor, FRAME_COUNT, FRAME_INTERVAL_MINUTES, HISTORY_FRAME_COUNT),
    [timeAnchor],
  );
  const playbackFrameIntervalMs = useMemo(
    () =>
      getPlaybackFrameIntervalMs(playbackSpeed, {
        playbackIntervalBySpeed: DOPPLER_CONFIG.playbackIntervalBySpeed,
        basePlaybackIntervalMs: DOPPLER_CONFIG.basePlaybackIntervalMs,
        minPlaybackIntervalMs: DOPPLER_CONFIG.minPlaybackIntervalMs,
        maxPlaybackIntervalMs: DOPPLER_CONFIG.maxPlaybackIntervalMs,
      }),
    [playbackSpeed],
  );

  useEffect(() => {
    const now = Date.now();
    const nextBoundary = Math.ceil(now / FRAME_INTERVAL_MS) * FRAME_INTERVAL_MS;
    const delay = Math.max(1000, nextBoundary - now + 250);

    const timeoutId = window.setTimeout(() => {
      setTimeAnchor(roundToNearestIntervalMinutes(new Date(), FRAME_INTERVAL_MINUTES));
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [timeAnchor]);

  useEffect(() => {
    const currentFrameIndex = Math.min(CURRENT_FRAME_INDEX, Math.max(frames.length - 1, 0));
    setDisplayedFrameIndex(currentFrameIndex);
    setRequestedFrameIndex(currentFrameIndex);
    setIsTransitioning(false);
  }, [frames]);

  useEffect(() => {
    if (
      !playbackActive ||
      !isPlaying ||
      !isPlaybackReady ||
      isTransitioning ||
      requestedFrameIndex !== displayedFrameIndex ||
      !frames.length
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setRequestedFrameIndex((displayedFrameIndex + 1) % frames.length);
    }, playbackFrameIntervalMs);

    return () => window.clearTimeout(timeoutId);
  }, [
    displayedFrameIndex,
    frames.length,
    isPlaybackReady,
    isPlaying,
    isTransitioning,
    playbackActive,
    playbackFrameIntervalMs,
    requestedFrameIndex,
  ]);

  const requestFrame = useCallback(
    (frameIndex) => {
      if (!frames.length) {
        return;
      }

      const normalizedIndex = ((frameIndex % frames.length) + frames.length) % frames.length;
      setRequestedFrameIndex(normalizedIndex);
    },
    [frames.length],
  );

  const handleDisplayedFrameChange = useCallback((frameIndex) => {
    setDisplayedFrameIndex(frameIndex);
    setRequestedFrameIndex(frameIndex);
  }, []);

  return {
    baseTime: timeAnchor,
    frames,
    displayedFrameIndex,
    requestedFrameIndex,
    isPlaying,
    playbackSpeed,
    setIsPlaying,
    setPlaybackSpeed,
    requestFrame,
    handleDisplayedFrameChange,
    setIsTransitioning,
  };
}
